import React, { useState, useRef } from 'react';
import styles from '../../assets/styles/Admin.module.scss';
import { mockClients, mockPayments, mockInquiries } from '../../mock/admin_mock';
import { useUser } from '../../contexts/UserContext';

const AdminDashboard: React.FC = () => {
  const { user } = useUser();
  const [isUploadingJaGeo, setIsUploadingJaGeo] = useState(false);
  const [jaGeoUploadMessage, setJaGeoUploadMessage] = useState<string | null>(null);
  const [isUploadingGoogleGeo, setIsUploadingGoogleGeo] = useState(false);
  const [googleGeoUploadMessage, setGoogleGeoUploadMessage] = useState<string | null>(null);
  const jaGeoFileInputRef = useRef<HTMLInputElement>(null);
  const googleGeoFileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadJaGeoCsv = async () => {
    const file = jaGeoFileInputRef.current?.files?.[0];
    if (!file) {
      setJaGeoUploadMessage('ファイルを選択してください。');
      return;
    }

    setIsUploadingJaGeo(true);
    setJaGeoUploadMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/upload-ja-geo-targets`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'アップロードに失敗しました');
      }

      await res.json();
      setJaGeoUploadMessage('成功: 日本郵政CSVの処理を開始しました。jaマスタ生成後に Google Geo Targets CSV をアップロードしてください。');
      if (jaGeoFileInputRef.current) jaGeoFileInputRef.current.value = "";
    } catch (e: any) {
      setJaGeoUploadMessage(`エラー: ${e.message}`);
    } finally {
      setIsUploadingJaGeo(false);
    }
  };

  const handleUploadGoogleGeoCsv = async () => {
    const file = googleGeoFileInputRef.current?.files?.[0];
    if (!file) {
      setGoogleGeoUploadMessage('ファイルを選択してください。');
      return;
    }

    setIsUploadingGoogleGeo(true);
    setGoogleGeoUploadMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/upload-geo-targets`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'アップロードに失敗しました');
      }

      await res.json();
      setGoogleGeoUploadMessage('成功: Google Geo Targets CSV の処理を開始しました。jaマスタとの突合後、数秒後に「プロジェクト設定」画面で最新のエリア選択が利用可能になります。');
      if (googleGeoFileInputRef.current) googleGeoFileInputRef.current.value = "";
    } catch (e: any) {
      setGoogleGeoUploadMessage(`エラー: ${e.message}`);
    } finally {
      setIsUploadingGoogleGeo(false);
    }
  };

  return (
    <div>
      <div className={styles.pageHeader}>
        <h1>ダッシュボード <span className={styles.mockNotice}>(moc)</span></h1>
        <p>システム全体の利用状況概要</p>
      </div>

      <div className={styles.dashboardGrid}>
        <div className={`${styles.contentBox} ${styles.dashboardCard}`}>
          <h3>登録クライアント数</h3>
          <p className={styles.statValueSuccess}>{mockClients.length}</p>
        </div>

        <div className={`${styles.contentBox} ${styles.dashboardCard}`}>
          <h3>未払い・保留中の決済</h3>
          <p className={styles.statValueWarning}>
            {mockPayments.filter(p => p.status === 'pending').length}
          </p>
        </div>

        <div className={`${styles.contentBox} ${styles.dashboardCard}`}>
          <h3>未対応のお問い合わせ</h3>
          <p className={styles.statValueError}>
            {mockInquiries.filter(i => i.status === 'open').length}
          </p>
        </div>
      </div>


      <div className={`${styles.contentBox} ${styles.geoUpdateBox}`}>
        <h3>日本郵政 CSV 更新</h3>
        <p>日本郵政のローマ字郵便番号CSVをアップロードして、都道府県・市区町村の日本語マスタを生成します。</p>
        <p className={styles.geoUpdateLinkWrapper}>
          <a href="https://www.post.japanpost.jp/service/search/zipcode/download/readme_ro.html" target="_blank" rel="noopener noreferrer" className={styles.geoUpdateLink}>
            🔗 日本郵便 郵便番号データ（ローマ字）の説明
          </a>
        </p>
        <div className={styles.geoUpdateForm}>
          <input type="file" accept=".csv" ref={jaGeoFileInputRef} />
          <button
            className={styles.uploadButton}
            onClick={handleUploadJaGeoCsv}
            disabled={isUploadingJaGeo}
          >
            {isUploadingJaGeo ? 'アップロード中...' : 'アップロード'}
          </button>
        </div>
        {jaGeoUploadMessage && (
          <p className={jaGeoUploadMessage.startsWith('エラー') ? styles.uploadMessageError : styles.uploadMessageSuccess}>
            {jaGeoUploadMessage}
          </p>
        )}
      </div>

      <div className={`${styles.contentBox} ${styles.geoUpdateBox}`}>
        <h3>Google Geo Targets CSV 更新</h3>
        <p>Google Ads のターゲットエリアCSVをアップロードして、GEOマスタを生成します。日本郵政CSV由来の jaマスタがある場合は日本語ラベルを付与します。</p>
        <p className={styles.geoUpdateLinkWrapper}>
          <a href="https://developers.google.com/google-ads/api/data/geotargets" target="_blank" rel="noopener noreferrer" className={styles.geoUpdateLink}>
            🔗 Google Ads API Geo Targets (CSVダウンロードはこちら)
          </a>
        </p>
        <div className={styles.geoUpdateForm}>
          <input type="file" accept=".csv" ref={googleGeoFileInputRef} />
          <button
            className={styles.uploadButton}
            onClick={handleUploadGoogleGeoCsv}
            disabled={isUploadingGoogleGeo}
          >
            {isUploadingGoogleGeo ? 'アップロード中...' : 'アップロード'}
          </button>
        </div>
        {googleGeoUploadMessage && (
          <p className={googleGeoUploadMessage.startsWith('エラー') ? styles.uploadMessageError : styles.uploadMessageSuccess}>
            {googleGeoUploadMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
